<!-- CrudDisenos.vue — Sección Ventas → Diseños -->
<!-- Maneja: carga, búsqueda, sort, paginación, alta/edición, borrado y export -->
<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Diseños"
      v-model="filtro"
      searchPlaceholder="Cotización o cliente..."
    >
      <template #actions>
        <button class="btn btn-exportar btn-header" @click="exportarExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span class="btn-header-texto ms-2">Exportar Excel</span>
        </button>
        <button class="btn btn-nuevo btn-header" @click="modalNuevo.abrir()">
          <i class="bi bi-vector-pen"></i>
          <span class="btn-header-texto ms-2">Nueva preaprobación</span>
        </button>
      </template>
    </CrudToolbar>

    <DisenosTabla
      :disenos="disenosPaginados"
      :loading="loading"
      :paginaActual="paginaActual"
      :totalPaginas="totalPaginas"
      :totalRegistros="disenosOrdenados.length"
      :rangoInicio="rangoInicio"
      :rangoFin="rangoFin"
      :paginasVisibles="paginasVisibles"
      :sortKey="sortKey"
      :sortDir="sortDir"
      :porPagina="porPagina"
      :opcionesPorPagina="paginacionStore.opcionesPorPagina"
      @ver-pdfs="modalPdfs.abrir($event)"
      @ver-detalle="modalDetalle.abrir($event)"
      @editar="modalNuevo.abrir($event)"
      @eliminar="confirmarEliminar"
      @cambiar-pagina="paginaActual = $event"
      @update:sort="onSort"
      @cambiar-por-pagina="cambiarPorPagina"
    />

    <ModalNuevoDiseno
      ref="modalNuevo"
      :catClientes="catClientes"
      @guardado="cargarDisenos"
    />
    <ModalPdfsDiseno ref="modalPdfs" />
    <ModalDetalleDiseno ref="modalDetalle" @notificado="cargarDisenos" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import Swal from "sweetalert2";
import {
  getDisenos,
  eliminarDiseno,
  exportarDisenos,
  getClientes,
} from "@/utils/api_backend";
import { suscribirWhatsapp } from "@/utils/ws_whatsapp";
import { userPaginacionStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";

import DisenosTabla from "./DisenosTabla.vue";
import ModalNuevoDiseno from "./ModalNuevoDiseno.vue";
import ModalPdfsDiseno from "./ModalPdfsDiseno.vue";
import ModalDetalleDiseno from "./ModalDetalleDiseno.vue";

const paginacionStore = userPaginacionStore();

const modalNuevo = ref(null);
const modalPdfs = ref(null);
const modalDetalle = ref(null);

const disenos = ref([]);
const catClientes = ref([]);
const loading = ref(false);

// ── Filtro ────────────────────────────────────────────────
// Se filtra en memoria, no contra el endpoint: el volumen de preaprobaciones
// es bajo y así la búsqueda responde sin ida y vuelta al servidor.
const filtro = ref("");

const disenosFiltrados = computed(() => {
  const q = filtro.value.trim().toLowerCase();
  if (!q) return disenos.value;
  return disenos.value.filter(
    (d) =>
      d.cotizacion?.toLowerCase().includes(q) ||
      d.cliente?.toLowerCase().includes(q) ||
      String(d.cliente_numero || "").toLowerCase().includes(q)
  );
});

// ── Ordenamiento ──────────────────────────────────────────
const sortKey = ref("creado_en");
const sortDir = ref("desc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

const disenosOrdenados = computed(() =>
  [...disenosFiltrados.value].sort((a, b) => {
    const valA = a[sortKey.value] ?? "";
    const valB = b[sortKey.value] ?? "";
    const cmp = String(valA).localeCompare(String(valB), "es", { sensitivity: "base" });
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

// ── Paginación ────────────────────────────────────────────
const paginaActual = ref(1);
const porPagina = computed(() => paginacionStore.porPagina);

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

watch(filtro, () => {
  paginaActual.value = 1;
});

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(disenosOrdenados.value.length / porPagina.value))
);

const disenosPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return disenosOrdenados.value.slice(inicio, inicio + porPagina.value);
});

const rangoInicio = computed(() =>
  disenosOrdenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, disenosOrdenados.value.length)
);

const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

// ── Carga ─────────────────────────────────────────────────
async function cargarDisenos() {
  loading.value = true;
  try {
    disenos.value = await getDisenos();
  } catch {
    Swal.fire("Error", "No se pudieron cargar las preaprobaciones.", "error");
  } finally {
    loading.value = false;
  }
}

async function cargarClientes() {
  try {
    catClientes.value = await getClientes();
  } catch {
    // El catálogo solo hace falta para el alta; si falla, el resto sigue usable.
    console.error("No se pudo cargar el catálogo de clientes");
  }
}

// ── Eliminar ──────────────────────────────────────────────
async function confirmarEliminar(diseno) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: `¿Eliminar ${diseno.cotizacion}?`,
    text: "Se borrarán también sus documentos.",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (!isConfirmed) return;

  try {
    await eliminarDiseno(diseno.id);
    await cargarDisenos();
    Swal.fire({
      icon: "success",
      title: "Registro eliminado",
      timer: 1400,
      showConfirmButton: false,
    });
  } catch (err) {
    Swal.fire(
      "Error",
      err.response?.data?.detail || "No se pudo eliminar el registro.",
      "error"
    );
  }
}

// ── Exportar ──────────────────────────────────────────────
async function exportarExcel() {
  try {
    await exportarDisenos();
  } catch {
    Swal.fire("Error", "No se pudo generar el archivo.", "error");
  }
}

// ── Estatus de WhatsApp en vivo ───────────────────────────
// El "entregado" o "leído" puede llegar minutos después del envío, cuando el
// usuario ya no está mirando. El webhook lo empuja por WebSocket y aquí solo
// se repinta el renglón, sin recargar la lista.
let bajaWhatsapp = null;

function escucharWhatsapp() {
  bajaWhatsapp = suscribirWhatsapp((evento) => {
    // "estatus": entrega del mensaje de WhatsApp (enviado/leído/fallido).
    if (evento.tipo === "estatus") {
      const { preaprobacion_id: id, estatus_preaprobacion: estatus } = evento.mensaje;
      if (!id || !estatus) return; // es de un pedido, no de un diseño

      const registro = disenos.value.find((d) => d.id === id);
      if (registro) registro.estatus_whatsapp = estatus;
      return;
    }

    // "diseno_respuesta": el cliente aprobó o rechazó desde el link público.
    if (evento.tipo === "diseno_respuesta") {
      const registro = disenos.value.find((d) => d.id === evento.preaprobacion_id);
      if (registro) registro.estatus = evento.estatus;
    }
  });
}

onMounted(() => {
  cargarDisenos();
  cargarClientes();
  escucharWhatsapp();
});

onUnmounted(() => {
  if (bajaWhatsapp) bajaWhatsapp();
});
</script>

<style scoped>
/*
 * Los botones del toolbar se estilan por componente en todo el sistema
 * (CrudTpus, CrudPedidos...), no hay una hoja global. Se replican aquí para
 * que la sección no desentone.
 */
.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-exportar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-exportar:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
}
</style>
